export interface CreateBaiduWmsLayerConfig {
  url: string;
  params: Record<string, any>;
  layerId: string;
  debug?: boolean;
}

export const createWmsParams = (params: Record<string, any>) => {
  return new URLSearchParams({
    REQUEST: 'GetMap',
    SERVICE: 'WMS',
    VERSION: '1.3.0',
    CRS: 'EPSG:4326',
    WIDTH: '512',
    HEIGHT: '512',
    FORMAT: 'image/png',
    TRANSPARENT: 'true',
    ...params,
  });
};

export const createWmsBBoxTemplate = () => {
  return (x: number, y: number, z: number) => {
    const tileToLon = (tileX: number) => (tileX / Math.pow(2, z)) * 360 - 180;
    const tileToLat = (tileY: number) => {
      const n = Math.PI - (2 * Math.PI * tileY) / Math.pow(2, z);
      return (180 / Math.PI) * Math.atan(Math.sinh(n));
    };
    const west = tileToLon(x);
    const east = tileToLon(x + 1);
    const north = tileToLat(y);
    const south = tileToLat(y + 1);
    return `${south},${west},${north},${east}`;
  };
};

export const createBaiduWmsLayer = (
  map: any,
  config: CreateBaiduWmsLayerConfig
) => {
  const params = createWmsParams(config.params);
  const createTileLayer = () =>
    new window.BMapGL.XYZLayer({
      spanLevel: -1,
      thumbUpsample: true,
      preciseVertex: true,
      useThumbData: true,
      transparentPng: true,
      tileUrlTemplate: `${config.url}?${params.toString()}&BBOX=[b]`,
      bTemplate: createWmsBBoxTemplate(),
      // 使用 fetch 带 credentials 请求瓦片，解决微应用/iframe 下 cookie 被 SameSite 拦截问题
      tileLoadFunction: (img: HTMLImageElement, src: string) => {
        fetch(src, {
          credentials: 'include',
          mode: 'cors',
        })
          .then((res) => res.blob())
          .then((blob) => {
            img.src = URL.createObjectURL(blob);
          })
          .catch(() => {
            // 回退到直接加载，避免图片完全不显示
            img.crossOrigin = 'use-credentials';
            img.src = src;
          });
      },
    });

  const xyzLayer = createTileLayer();
  let visible = true;
  let mounted = false;

  if (config.debug) {
    window.wms = xyzLayer;
  }

  const mountLayer = () => {
    if (mounted || !visible) {
      return;
    }
    map.addTileLayer(xyzLayer);
    mounted = true;
  };

  const unmountLayer = () => {
    if (!mounted) {
      return;
    }
    map.removeTileLayer(xyzLayer);
    mounted = false;
  };

  return {
    layerId: config.layerId,
    visible,
    addToMap: () => mountLayer(),
    setVisible: (nextVisible: boolean) => {
      visible = nextVisible;
      if (visible) {
        mountLayer();
        return;
      }
      unmountLayer();
    },
    clear: () => unmountLayer(),
    getSource: () => ({
      clear: () => unmountLayer(),
    }),
  };
};
